import { setCookie } from "app/utils/cookie";
import { localGet, localSet } from "app/utils/LocalStorage";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

export const CLICK_ID_KEYS = ["gclid", "fbclid"] as const;
export type ClickIdKey = (typeof CLICK_ID_KEYS)[number];

/** localStorage key holding the epoch-ms moment a click id was first captured. */
export const capturedAtKey = (key: ClickIdKey): string => `${key}_captured_at`;

/**
 * The capture time is the backend's proxy for the click time (Google honours a
 * gclid for 90 days from the click), so it is written only when the id is new.
 * coachnow.com re-appends the same id from its cookie on later visits; restamping
 * then would make an old click look fresh. Click ids are unique per click.
 */
const captureClickId = (key: ClickIdKey, value: string): void => {
  if (localGet(key) === value) {
    return;
  }
  localSet(key, value);
  localSet(capturedAtKey(key), Date.now());
};

/**
 * Which coachnow.com page and button sent the visitor to signup (CN-2742).
 * Kept for the browser tab, so every signup funnel event can carry them,
 * including the ones after the redirect to a trial page.
 */
const ENTRY_KEYS = ["entry_page", "entry_cta"] as const;
export type EntryParams = { entry_page?: string; entry_cta?: string };

const captureEntryParams = (params: URLSearchParams): void => {
  ENTRY_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) {
      try {
        sessionStorage.setItem(key, value);
      } catch {
        // storage blocked: the events go out without the entry params
      }
    }
  });
};

export const getEntryParams = (): EntryParams => {
  const params: EntryParams = {};
  ENTRY_KEYS.forEach((key) => {
    try {
      const value = sessionStorage.getItem(key);
      if (value) {
        params[key] = value;
      }
    } catch {
      // storage blocked
    }
  });
  return params;
};

export const captureUTMParams = (): void => {
  const searchParams = new URLSearchParams(window.location.search);

  UTM_KEYS.forEach((key) => {
    const value = searchParams.get(key);
    if (value) {
      setCookie(key, value, 3650);
    }
  });

  CLICK_ID_KEYS.forEach((key) => {
    const value = searchParams.get(key);
    if (value) {
      captureClickId(key, value);
    }
  });

  captureEntryParams(searchParams);

  // Signup honors both spellings of the return url.
  const returnUrl = searchParams.get("return_url") || searchParams.get("returnUrl");
  if (returnUrl?.includes("?")) {
    const returnParams = new URLSearchParams(returnUrl.split("?")[1]);
    UTM_KEYS.forEach((key) => {
      const value = returnParams.get(key);
      if (value) {
        setCookie(key, value, 3650);
      }
    });
    captureEntryParams(returnParams);
  }
};
